import Pubnub, { ObjectCustom, UUIDMetadata } from 'pubnub'
import { PubNubProvider } from 'pubnub-react'
import { Suspense, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import Footer from './components/footer/Footer'
import Loader from './components/loader/Loader'
import Navbar from './components/navbar/Navbar'
import ProfileContent from './components/personal/ProfileContent'
import ProfileForm from './components/personal/ProfileForm'
import { PrivateRoute } from './containers/PrivateRoutes'
import { lazyWithRetry } from './helpers/lazyWIthRetry'
import Countdown from './pages/countdown/Countdown'
import Login from './pages/login/Login'
import Networking from './pages/networking/Networking'
import Personal from './pages/personal/Personal'
import ResetPassword from './pages/resetpassword/ResetPassword'
import SetNewPassword from './pages/resetpassword/SetNewPassword'
import JoinZoom from './pages/sessions/joinZoom/JoinZoom'
import LiveSessions from './pages/sessions/livesessions/LiveSessions'
import OnDemandSessions from './pages/sessions/onDemand/OnDemandSessions'
import OnDemands from './pages/sessions/OnDemands'
import RecordedSessions from './pages/sessions/recordedsessions/RecordedSessions'
import RecordedSessionsAccess from './pages/sessions/onDemand/OnDemandShow'
import Sessions from './pages/sessions/Sessions'
import routes from './routes'
import OnDemandShow from './pages/sessions/onDemand/OnDemandShow'

const ZoomContainer = lazyWithRetry(() => import('./pages/zoom/ZoomContainer'))
const ZoomPage = lazyWithRetry(() => import('./pages/zoom/ZoomPage'))

const Root = () => {
  const { isAuthenticated } = useSelector((state: any) => state.auth)

  if (isAuthenticated) {
    return <Navigate to={routes.onDemandSessions} />
  }

  return <Navigate to={routes.login} />
}

export const Layout = (props: any) => {
  const { currentUser } = useSelector((state: any) => state.auth)

  // Due to PrivateRoute, currentUser is always defined
  const pubnub = useMemo(
    () =>
      new Pubnub({
        publishKey: 'pub-c-2188d2c7-47b8-4c5e-a276-431d059f5edf',
        subscribeKey: 'sub-c-83f40d36-6c18-4392-ac40-f8779fe7efa7',
        userId: `${currentUser?.id}`,
        heartbeatInterval: 30,
      }),
    [currentUser?.id]
  )

  useEffect(() => {
    const listeners = {
      // message: (m: any) => {
      //   console.log([m.channel, m.subscription, m.message, m.publisher])
      //   if (m.channel === 'lobby') {
      //     const receiverId = m.message
      //       .split('-')
      //       .find((id: string) => id !== m.publisher)
      //     console.log({ receiverId })
      //   }
      // },
      // presence: (m: any) => {
      //   console.log([m.channel, m.action])
      // },
      signal: (m: any) => {
        console.log([m.channel, m.subscription, m.message, m.publisher])
        if (m.channel === 'lobby') {
          const receiverId = m.message
            .split('-')
            .find((id: string) => id !== m.publisher)
          console.log({ receiverId })
        }
      },
    }

    if (currentUser) {
      pubnub.setState(
        {
          state: {
            name: currentUser.fullName,
            email: currentUser.email,
            companyName: currentUser.companyName,
            profileUrl: `https://ui-avatars.com/api/?name=${currentUser.fullName}&background=951d31&color=fff`,
          },
          channels: ['lobby'],
        },
        () => {
          pubnub.subscribe({ channels: ['lobby'], withPresence: true })
          pubnub.objects.setUUIDMetadata({
            data: {
              uuid: `${currentUser.id}`,
              name: currentUser.fullName,
              email: currentUser.email,
              companyName: currentUser.companyName,
              profileUrl: `https://ui-avatars.com/api/?name=${currentUser.fullName}&background=951d31&color=fff`,
            } as UUIDMetadata<ObjectCustom>,
          })
          pubnub.addListener(listeners)
        }
      )
    }
    return () => {
      pubnub.unsubscribeAll()
      pubnub.removeListener(listeners)
    }
  }, [currentUser, pubnub])

  return (
    <PubNubProvider client={pubnub}>
      <Navbar />
      <div className="container-fluid g-0">
        <Outlet />
      </div>
      <Footer />
    </PubNubProvider>
  )
}

const ZoomExit = () => {
  useEffect(() => {
    // window.location.href = appConfig.EVENT_URL + '/agenda'
    window.parent.postMessage('LEAVE_ZOOM')
  }, [])
  return <div>Exit</div>
}

const Views = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={routes.zoom} element={<ZoomPage />} />
        <Route path={routes.countdown} element={<Countdown />} />
        <Route element={<Layout />}>
          <Route path={routes.root} element={<Root />} />
          <Route path={routes.login} element={<Login />} />
          <Route path={routes.reset} element={<ResetPassword />} />
          <Route path={routes.createPassword} element={<SetNewPassword />} />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route path={routes.joinSession} element={<ZoomContainer />} />
          <Route path={routes.leaveSession} element={<ZoomExit />} />
          <Route path={routes.networking} element={<Networking />} />
          <Route path={routes.personal} element={<Personal />}>
            <Route path={routes.profile} element={<ProfileContent />} />
            <Route path={routes.editProfile} element={<ProfileForm />} />
          </Route>
          <Route path={routes.sessions} element={<Sessions />}>
            <Route
              path={'/sessions/live-sessions/:category'}
              element={<LiveSessions />}
            />
            <Route path={routes.liveSessions} element={<LiveSessions />} />

          </Route>
          <Route path={routes.onDemand} element={<OnDemands />}>
            <Route
              path={`/on-demand/:category`}
              element={<OnDemandSessions />}
            />
            <Route
              path={routes.onDemandSessions}
              element={<OnDemandSessions />}
            />
          </Route>
          <Route
            path={routes.onDemandShow}
            element={<OnDemandShow />}
          />
          <Route
            path="/sessions/recorded-sessions/:id"
            element={<RecordedSessionsAccess />}
          />
          <Route path={'/join-zoom/:sessionId'} element={<JoinZoom />} />
          <Route path={'/join-zoom'} element={<JoinZoom />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default Views
